import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from '@mui/material';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DuplicateFieldsDialog from '../../common/DuplicateFieldsDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FieldActions from '../../common/FieldActions';
import FieldList from './FieldList';
import FieldNew from '../../fields/FieldNew';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { IDocument, IField, IGroundTruth, ITrainingSet } from '../../../types/my-types';
import { featureFlags } from '../../../utils/featureFlags';

import MarkReviewFormStyles from './MarkReviewForm.module.scss';

export interface MarkFormProps {
  doc: IDocument;
  fields: IField[];
  groundTruths: IGroundTruth[];
  trainingSet: ITrainingSet;
  on: boolean;
  limit: number;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
  setOn: React.Dispatch<React.SetStateAction<boolean>>;
}

const MarkForm: React.FC<MarkFormProps> = ({
  doc,
  fields,
  trainingSet,
  groundTruths,
  on,
  limit,
  setLimit,
  setOn,
}) => {
  const [isAddFieldActive, setAddFieldActive] = useState(false);
  const [newFieldHeight, setNewFieldHeight] = useState(0);
  const newFieldRef = React.useRef<HTMLDivElement>(null);

  // Dynamically set the marginTop of the fieldList container based on the newField height
  React.useEffect(() => {
    if (isAddFieldActive && newFieldRef.current) {
      setNewFieldHeight(newFieldRef.current.offsetHeight);
    } else {
      setNewFieldHeight(0);
    }
  }, [isAddFieldActive]);

  const onToggleMarkMode = () => {
    if (on) {
      limit === 100 ? setLimit(99) : setLimit(100);
    }
    setOn((on) => !on);
  };

  return (
    <>
      <div id="mark-form" className={MarkReviewFormStyles.formBaseStyles} data-cy="mark-form">
        {featureFlags.duplicateFields ? (
          <>
            <FieldActions
              isAddFieldActive={isAddFieldActive}
              isMarkModeActive={on}
              isMarkPage={true}
              onToggleMarkMode={onToggleMarkMode}
              setAddFieldActive={setAddFieldActive}
            />

            {/* 'display' CSS property required for Cypress testing; element must be in DOM */}
            <div
              ref={newFieldRef}
              className={`${MarkReviewFormStyles.formBaseStyles} ${MarkReviewFormStyles.newFieldContainer}`}
              style={{
                display: isAddFieldActive ? 'block' : 'none',
              }}
            >
              <FieldNew
                cypressId="input-mark-new-field"
                fieldLabels={fields.map((field) => field.label)}
                trainingSetId={trainingSet.id}
              />
            </div>
          </>
        ) : (
          <Accordion>
            <AccordionSummary
              data-cy="btn-new-field"
              expandIcon={<ExpandMoreIcon />}
              id="mark-form-panel"
            >
              <Typography style={{ paddingRight: 15 }}>New Field</Typography>
              <AddCircleOutline />
            </AccordionSummary>
            <AccordionDetails>
              <div className={MarkReviewFormStyles.formBaseStyles}>
                <FieldNew
                  cypressId="input-mark-new-field"
                  fieldLabels={fields.map((field) => field.label)}
                  trainingSetId={trainingSet.id}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        )}

        <div
          data-cy="field-list-container"
          className={MarkReviewFormStyles.fieldListContainer}
          style={{
            height: `calc(100% - 63px
              ${newFieldHeight !== 0 ? ` - ${newFieldHeight}px` : ''})`,
          }}
        >
          <FieldList
            doc={doc}
            fields={fields}
            groundTruths={groundTruths}
            trainingSetId={trainingSet.id}
          />
        </div>
      </div>

      <DuplicateFieldsDialog
        fieldLabels={fields.map((field) => field.label)}
        trainingSetId={trainingSet.id}
      />
    </>
  );
};

export default MarkForm;
